import ProductStore from './ProductStore';
import type { RouteType } from '../../types/RouteType';
import { RootStore } from '@intrasync/stores';

const ProductRoute: RouteType = {
  name: 'Product Management',
  path: '/product/:tab',
  defaultParams: {
    tab: 'list'
  },
  layout: '/app',
  viewportCard: {
    header: {
      component: () => import('./Product.Header'),
      title: 'Product Management',
      color: 'primary',
      enableRefreshButton: true,
      enableBackButton: false
    },
    body: {
      component: () => import('./Product.Body'),
      grayBackground: true
    }
  },
  group: 'Product',
  requiresAuth: true,
  requiresPermission: false,
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    const productStore = rootStore.productStore as ProductStore

    // Load categories for navigation
    if (productStore && typeof productStore.loadCategories === 'function') {
      await productStore.loadCategories();
    }

    // Always load products for list view
    if (productStore && typeof productStore.loadProducts === 'function') {
      await productStore.loadProducts();
    }

    return true;
  },
  onParamsChange: async ({ rootStore }: { rootStore: RootStore }) => {
    const productStore = rootStore.productStore as ProductStore;

    // Load categories for navigation
    if (productStore && typeof productStore.loadCategories === 'function') {
      await productStore.loadCategories();
    }

    // Always load products for list view
    if (productStore && typeof productStore.loadProducts === 'function') {
      await productStore.loadProducts();
    }
  },
  dialogs: [
    {
      name: 'createProduct',
      component: () => import('./ProductDialogs/ProductForm.Dialog'),
      title: 'Create Product',
      onSubmit: async ({ rootStore, data, closeDialog }: any) => {
        try {
          const newProduct = await rootStore.productStore.createProduct(data);
          await rootStore.productStore.loadProducts();
          closeDialog();
          return newProduct;
        } catch (error) {
          throw error;
        }
      },
      onSubmitValidation: ({ data }: any) => {
        return !!(data.productCode && data.name && data.productType);
      },
      cancelButton: true,
      height: 600
    },
    {
      name: 'editProduct',
      component: () => import('./ProductDialogs/ProductForm.Dialog'),
      title: 'Edit Product',
      onSubmit: async ({ rootStore, data, closeDialog }: any) => {
        try {
          const productId = rootStore.productViewStore.editingProduct?.id;
          if (!productId) throw new Error('No product selected for editing');

          const updatedProduct = await rootStore.productStore.updateProduct(productId, data);
          closeDialog();
          return updatedProduct;
        } catch (error) {
          throw error;
        }
      },
      onSubmitValidation: ({ data }: any) => {
        return !!(data.productCode && data.name && data.productType);
      },
      cancelButton: true,
      height: 600
    },
    {
      name: 'productDetails',
      component: () => import('./ProductDialogs/ProductDetail.Dialog'),
      title: 'Product Details',
      cancelButton: true,
      closeButton: true,
      height: 800,
      fullWidth: true
    },
    {
      name: 'createVariant',
      component: () => import('./ProductDialogs/ProductVariantForm.Dialog'),
      title: 'Create Product Variant',
      onSubmit: async ({ rootStore, data, closeDialog }: any) => {
        try {
          const productId = rootStore.productViewStore.selectedProductId;
          if (!productId) throw new Error('No product selected');

          const newVariant = await rootStore.productStore.createVariant({
            ...data,
            productId
          });
          await rootStore.productStore.loadVariants(productId);
          closeDialog();
          return newVariant;
        } catch (error) {
          throw error;
        }
      },
      onSubmitValidation: ({ data }: any) => {
        return !!(data.variantCode && data.name);
      },
      cancelButton: true,
      height: 500
    },
    {
      name: 'createBOM',
      component: () => import('./ProductDialogs/BOMForm.Dialog'),
      title: 'Create Bill of Materials',
      onSubmit: async ({ rootStore, data, closeDialog }: any) => {
        try {
          const productId = rootStore.productViewStore.selectedProductId;
          if (!productId) throw new Error('No product selected');

          const newBOM = await rootStore.productStore.createBOM({
            ...data,
            productId
          });
          await rootStore.productStore.loadBOMs(productId);
          closeDialog();
          return newBOM;
        } catch (error) {
          throw error;
        }
      },
      onSubmitValidation: ({ data }: any) => {
        return !!(data.bomCode && data.name && data.version);
      },
      cancelButton: true,
      height: 500
    },
    {
      name: 'addBOMItem',
      component: () => import('./ProductDialogs/BOMItemForm.Dialog'),
      title: 'Add BOM Item',
      onSubmit: async ({ rootStore, data, closeDialog }: any) => {
        try {
          const bomId = rootStore.productViewStore.selectedBOMId;
          if (!bomId) throw new Error('No BOM selected');

          const newItem = await rootStore.productStore.createBOMItem({
            ...data,
            bomId
          });
          await rootStore.productStore.loadBOMItems(bomId);
          closeDialog();
          return newItem;
        } catch (error) {
          throw error;
        }
      },
      onSubmitValidation: ({ data }: any) => {
        return !!(data.itemCode && data.name && data.quantity && data.unit && data.itemType);
      },
      cancelButton: true,
      height: 600
    },
    {
      name: 'addPricing',
      component: () => import('./ProductDialogs/PricingForm.Dialog'),
      title: 'Add Pricing Rule',
      onSubmit: async ({ rootStore, data, closeDialog }: any) => {
        try {
          const productId = rootStore.productViewStore.selectedProductId;
          if (!productId) throw new Error('No product selected');

          const newPricing = await rootStore.productStore.createPricingRule({
            ...data,
            productId,
            variantId: rootStore.productViewStore.selectedVariantId || undefined
          });
          await rootStore.productStore.loadPricing(productId, rootStore.productViewStore.selectedVariantId || undefined);
          closeDialog();
          return newPricing;
        } catch (error) {
          throw error;
        }
      },
      onSubmitValidation: ({ data }: any) => {
        return !!(data.priceType && data.price && data.effectiveDate);
      },
      cancelButton: true,
      height: 600
    },
    {
      name: 'addInventory',
      component: () => import('./ProductDialogs/InventoryForm.Dialog'),
      title: 'Add Inventory Location',
      onSubmit: async ({ rootStore, data, closeDialog }: any) => {
        try {
          const productId = rootStore.productViewStore.selectedProductId;
          if (!productId) throw new Error('No product selected');

          const newInventory = await rootStore.productStore.createInventoryRecord({
            ...data,
            productId,
            variantId: rootStore.productViewStore.selectedVariantId || undefined
          });
          await rootStore.productStore.loadInventory(productId, rootStore.productViewStore.selectedVariantId || undefined);
          closeDialog();
          return newInventory;
        } catch (error) {
          throw error;
        }
      },
      onSubmitValidation: ({ data }: any) => {
        return !!(data.location && data.unit && data.status);
      },
      cancelButton: true,
      height: 500
    },
    {
      name: 'recordMovement',
      component: () => import('./ProductDialogs/MovementForm.Dialog'),
      title: 'Record Inventory Movement',
      onSubmit: async ({ rootStore, data, closeDialog }: any) => {
        try {
          const { inventoryId, movementType, quantity, notes } = data;

          await rootStore.productStore.recordInventoryMovement(
            inventoryId,
            movementType,
            quantity,
            notes
          );

          const productId = rootStore.productViewStore.selectedProductId;
          if (productId) {
            await rootStore.productStore.loadInventory(productId, rootStore.productViewStore.selectedVariantId || undefined);
          }

          closeDialog();
        } catch (error) {
          throw error;
        }
      },
      onSubmitValidation: ({ data }: any) => {
        return !!(data.inventoryId && data.movementType && data.quantity !== undefined);
      },
      cancelButton: true,
      height: 400
    }
  ]
};

export default ProductRoute;